/**
 * Personal bests in `localStorage` — no server; Colyseus sessions can still record locally after a run.
 */

const STORAGE_KEY = 'last-mile-lb-v1';
const MAX_ROWS = 12;

export type LeaderboardRow = {
  at: number;
  stylePoints: number;
  timeS: number;
  breakers: number;
  maxMult: number;
};

function load(): LeaderboardRow[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is LeaderboardRow =>
        r &&
        typeof r === 'object' &&
        typeof (r as LeaderboardRow).stylePoints === 'number',
    );
  } catch {
    return [];
  }
}

function save(rows: LeaderboardRow[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    /* quota / private mode */
  }
}

export type RecordRunResult = {
  rank: number;
  isTopScore: boolean;
  board: LeaderboardRow[];
};

/**
 * Inserts this run into the sorted table (by style points, then faster time wins tiebreak).
 */
export function recordRun(
  stylePoints: number,
  timeS: number,
  pursuitBreakers: number,
  maxMult: number,
): RecordRunResult {
  const row: LeaderboardRow = {
    at: Date.now(),
    stylePoints: Math.max(0, Math.floor(stylePoints)),
    timeS,
    breakers: pursuitBreakers,
    maxMult,
  };

  const prev = load();
  const next = [...prev, row].sort((a, b) => {
    if (b.stylePoints !== a.stylePoints) return b.stylePoints - a.stylePoints;
    return a.timeS - b.timeS;
  });
  const trimmed = next.slice(0, MAX_ROWS);
  save(trimmed);

  const rank = trimmed.findIndex(
    (r) =>
      r.at === row.at &&
      r.stylePoints === row.stylePoints &&
      Math.abs(r.timeS - row.timeS) < 0.02,
  );
  return {
    rank: rank >= 0 ? rank + 1 : trimmed.length,
    isTopScore: rank === 0,
    board: trimmed,
  };
}

export function getLeaderboard(): LeaderboardRow[] {
  return load();
}
